import { LockIcon } from "@xola/icons";
import { Table } from "@xola/ui-kit";
import { DemographicIcon } from "common";
import { uniqBy } from "lodash";
import React from "react";
import { AdjustmentOperation, AdjustmentType, DemographicPrice } from "types";
import { isDefaultDemographic } from "../../../models/DemographicUnit";
import { Currency } from "../../Common/Currency";
import { NoTranslate } from "../../Common/NoTranslate";

interface PriceVariationTableProps {
    publicDemographicPrices: DemographicPrice[];
    privateDemographicPrices: DemographicPrice[];
    isPrivatePerOuting: boolean;
}

const getCatalogItems = (publicDemographicPrices: DemographicPrice[], privateDemographicPrices: DemographicPrice[]) => {
    const allDemographicPrices = [...publicDemographicPrices, ...privateDemographicPrices];
    const allCatalogItems = allDemographicPrices.map(
        (demographicPrice: DemographicPrice) => demographicPrice.catalogItem,
    );

    return uniqBy(allCatalogItems, "name").filter(Boolean);
};

const getDemographicPriceByCatalogType = (demographicPrices: DemographicPrice[], catalogItemName = "") => {
    return demographicPrices.find((demographicPrices) => demographicPrices?.catalogItem?.name === catalogItemName);
};

export const PriceVariationTable = ({
    publicDemographicPrices,
    privateDemographicPrices,
    isPrivatePerOuting,
}: PriceVariationTableProps) => {
    const shouldShowBothTerms =
        privateDemographicPrices.length > 0 && publicDemographicPrices.length > 0 && !isPrivatePerOuting;
    const catalogItems = getCatalogItems(publicDemographicPrices, privateDemographicPrices) ?? [];

    return (
        <Table>
            <Table.Head>
                <Table.Row>
                    <Table.Header>Demographics</Table.Header>
                    {shouldShowBothTerms ? (
                        <>
                            <Table.Header>Public Terms</Table.Header>
                            <Table.Header>Private Terms</Table.Header>
                        </>
                    ) : (
                        <Table.Header>Terms</Table.Header>
                    )}
                </Table.Row>
            </Table.Head>

            <Table.Body isStriped={false}>
                {catalogItems.map((catalogItem) => {
                    return (
                        <Table.Row key={catalogItem?.name}>
                            <Table.Cell>
                                <DemographicIcon className="mr-1" name={catalogItem?.name ?? ""} />
                                <NoTranslate if={!isDefaultDemographic(catalogItem?.name)}>
                                    {catalogItem?.name}
                                </NoTranslate>
                            </Table.Cell>

                            {shouldShowBothTerms ? (
                                <>
                                    <Table.Cell>
                                        <DemographicPriceTerm
                                            demographicPrice={getDemographicPriceByCatalogType(
                                                publicDemographicPrices,
                                                catalogItem?.name,
                                            )}
                                        />
                                    </Table.Cell>
                                    <Table.Cell>
                                        <DemographicPriceTerm
                                            demographicPrice={getDemographicPriceByCatalogType(
                                                privateDemographicPrices,
                                                catalogItem?.name,
                                            )}
                                        />
                                    </Table.Cell>
                                </>
                            ) : (
                                <Table.Cell>
                                    <DemographicPriceTerm
                                        demographicPrice={getDemographicPriceByCatalogType(
                                            publicDemographicPrices.length > 0
                                                ? publicDemographicPrices
                                                : privateDemographicPrices,
                                            catalogItem?.name,
                                        )}
                                    />
                                </Table.Cell>
                            )}
                        </Table.Row>
                    );
                })}

                {isPrivatePerOuting ? (
                    <Table.Row key="per-outing">
                        <Table.Cell>
                            <LockIcon className="mr-1" />
                            <span>Per Event</span>
                        </Table.Cell>
                        <Table.Cell>
                            <DemographicPriceTerm demographicPrice={privateDemographicPrices[0]} />
                        </Table.Cell>
                    </Table.Row>
                ) : null}
            </Table.Body>
        </Table>
    );
};

const DemographicPriceTerm = ({ demographicPrice }: { demographicPrice?: DemographicPrice }) => {
    const adjustmentAmount = demographicPrice?.adjustmentAmount ?? 0;
    return (
        <>
            {demographicPrice?.adjustmentType === AdjustmentType.PERCENT ? (
                <NoTranslate>{adjustmentAmount}%</NoTranslate>
            ) : (
                <Currency>{adjustmentAmount}</Currency>
            )}
            &nbsp;
            {demographicPrice?.adjustmentOperation === AdjustmentOperation.MARKUP
                ? "Markup"
                : demographicPrice?.adjustmentOperation === AdjustmentOperation.MARKDOWN
                ? "Off"
                : ""}
        </>
    );
};
